import { CreateDisplayGroupForm, ICreateDisplayGroupFormData } from '../components/CreateDisplayGroupForm'
import { FlexHorizontal, LoadMask, Spacer, useToasts } from '@vestaboard/installables'
import React, { useState } from 'react'

import { Typography } from '@material-ui/core'
import { useCreateBoard } from '../hooks/useCreateBoard'
import { useCreateDisplayGroup } from '../hooks/useCreateDisplayGroup'
import { useCreateDisplayGroupRole } from '../../note-array/hooks/useCreateDisplayGroupRole'
import { useCreateMessage } from '../hooks/useCreateMessage'
import { useCreateMessageAppearance } from '../hooks/useCreateMessageAppearance'
import {
  displayGroupRoleFailure,
  messageAppearanceFailure,
  mutationData,
  setupFailuresMessage,
  updateBoardFailure
} from '../setupFailures'
import { sleep } from '../../../utils/sleep'
import { useHistory } from 'react-router'
import { useUpdateBoard } from '../hooks/useUpdateBoard'

const BOARD_PROPAGATION_DELAY_MS = 3000

export const CreateDisplayGroupPage = () => {
  const history = useHistory()
  const { addToast } = useToasts()
  const [saving, setSaving] = useState(false)

  const [createBoard] = useCreateBoard()
  const [createDisplayGroup] = useCreateDisplayGroup()
  const [updateBoard] = useUpdateBoard()
  const [createDisplayGroupRole] = useCreateDisplayGroupRole()
  const [createMessage] = useCreateMessage()
  const [createMessageAppearance] = useCreateMessageAppearance()

  const handleSave = async (formData: ICreateDisplayGroupFormData) => {
    setSaving(true)
    try {
      const {
        title,
        rows,
        columns,
        timezoneId,
        quietHoursBegin,
        quietHoursEnd,
        members,
        firstMessageCharacters
      } = formData

      // Step 1: Create rows * columns boards
      const boardGrid: string[][] = []
      let boardCount = 0
      for (let r = 0; r < rows; r++) {
        const rowIds: string[] = []
        for (let c = 0; c < columns; c++) {
          boardCount++
          const boardResult = await createBoard({
            variables: {
              input: {
                title: `${title} ${boardCount}`,
                boardStyle: 'note-black'
              }
            }
          })
          const boardId = boardResult.data?.createBoard?.board?.id
          if (!boardId) {
            throw new Error(`Failed to create board at position [${r}, ${c}]`)
          }
          rowIds.push(boardId)
        }
        boardGrid.push(rowIds)
      }

      const allBoardIds = boardGrid.flat()

      await sleep(BOARD_PROPAGATION_DELAY_MS)

      // Step 2: Create the DisplayGroup
      const displayGroupResult = await createDisplayGroup({
        variables: {
          input: {
            title,
            boardIds: boardGrid
          }
        }
      })

      const displayGroupData = displayGroupResult.data?.createDisplayGroupV2
      if (!displayGroupData || displayGroupData.__typename === 'DisplayGroupErrorV2') {
        const errorMsg =
          displayGroupData?.__typename === 'DisplayGroupErrorV2'
            ? displayGroupData.error || 'Failed to create Note Array'
            : 'Failed to create Note Array'
        throw new Error(errorMsg)
      }

      const displayGroupId = displayGroupData.id
      const failures: (string | null)[] = []

      // Step 3: Update each board with Note properties (timezone, quiet hours)
      if (timezoneId || quietHoursBegin || quietHoursEnd) {
        for (const boardId of allBoardIds) {
          const updateResult = await mutationData(
            updateBoard({
              variables: {
                input: {
                  boardId,
                  boardStyle: 'note-black',
                  ...(timezoneId ? { timezone: timezoneId } : {}),
                  ...(quietHoursBegin ? { quietHoursBegin } : {}),
                  ...(quietHoursEnd ? { quietHoursEnd } : {})
                }
              }
            })
          )
          failures.push(updateBoardFailure(boardId, updateResult))
        }
      }

      // Step 4: Add members / invite
      for (const member of members) {
        if (member.email.trim()) {
          const roleResult = await mutationData(
            createDisplayGroupRole({
              variables: {
                input: {
                  groupId: displayGroupId,
                  email: member.email.trim(),
                  role: member.role as any
                }
              }
            })
          )
          failures.push(displayGroupRoleFailure(member.email.trim(), roleResult))
        }
      }

      // Step 5: Send first message to all boards if provided
      if (firstMessageCharacters) {
        const messageResult = await mutationData(
          createMessage({
            variables: {
              input: {
                characters: firstMessageCharacters as number[][]
              }
            }
          })
        )

        const messageData = messageResult?.createMessage
        if (!messageData || messageData.__typename === 'MessageErrorV2') {
          const errorMsg =
            messageData?.__typename === 'MessageErrorV2'
              ? messageData.error || 'Failed to create first message'
              : 'Failed to create first message'
          failures.push(errorMsg)
        } else {
          const messageId = messageData.id
          for (const boardId of allBoardIds) {
            const appearanceResult = await mutationData(
              createMessageAppearance({
                variables: {
                  input: {
                    boardId,
                    messageId
                  }
                }
              })
            )
            failures.push(messageAppearanceFailure(boardId, appearanceResult))
          }
        }
      }

      const failureMessage = setupFailuresMessage(failures)
      if (failureMessage) {
        addToast(failureMessage, { appearance: 'error', autoDismiss: false })
      } else {
        addToast('Note Array created successfully', { appearance: 'success', autoDismiss: true })
      }
      history.push(`/note-arrays/${displayGroupId}`)
    } catch (error) {
      addToast((error as any)?.message || 'There was an error creating the Note Array', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      {saving ? <LoadMask /> : null}
      <FlexHorizontal spaceBetween>
        <Typography variant='h4'>Create Note Array</Typography>
      </FlexHorizontal>
      <Spacer size='large' />
      <CreateDisplayGroupForm onSave={handleSave} saving={saving} />
    </>
  )
}
